import type { JSX } from 'react';
import { providerSchema } from '@voxler/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageChrome } from '../components/page-chrome';
import { useToast } from '../components/toast';
import { internalFetch, internalSend } from '../lib/api';
import type { Model, Provider } from '../lib/use-cases';
import { AddModelDialog, EditFeaturesDialog, FEATURE_LABELS } from './catalog/model-dialogs';
import { RateDialog } from './catalog/rate-dialog';
import {
  formatRate,
  formatRateChange,
  formatRateDate,
  formatRateDay,
  formatRateShort,
  latestRate,
  RATE_TREND_DAYS,
  rateOn,
  rateSourceLabel,
  shiftIsoDate,
  sortRatesDesc,
  type Rate,
} from './catalog/rates';
import { TariffsDialog } from './catalog/tariffs-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardFoot, CardHead } from '@/components/ui/card';
import { ChartLegend, TimeChart } from '@/components/ui/chart';
import { Row4 } from '@/components/ui/layout-grid';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';

type CatalogTab = 'models' | 'fx';
type ProviderFilter = Provider | 'all';

/** Capabilities shown in a row before the « +N » tag. */
const VISIBLE_FEATURES = 3;

const RATES_PAGE_SIZE = 8;

/** Capabilities of a row: the first three labels, the rest behind a « +N » tag. */
function FeatureTags({ model }: { model: Model }): JSX.Element {
  const labels = model.features.map((feature) => FEATURE_LABELS[feature]);
  const visible = labels.slice(0, VISIBLE_FEATURES);
  const rest = labels.slice(VISIBLE_FEATURES);
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((label) => (
        <Tag key={label}>{label}</Tag>
      ))}
      {rest.length > 0 ? (
        <Tag title={rest.join(', ')} aria-label={`et ${rest.join(', ')}`}>
          +{rest.length}
        </Tag>
      ) : null}
    </div>
  );
}

/** « Taux USD vers EUR » chart: ECB rates of the last 14 days up to the latest one. */
function RateChart({ rates }: { rates: readonly Rate[] }): JSX.Element | null {
  const ecb = rates.filter((rate) => rate.source === 'ecb');
  const last = latestRate(ecb);
  if (last === undefined) {
    return null;
  }
  const from = shiftIsoDate(last.date, -RATE_TREND_DAYS);
  const points = sortRatesDesc(ecb.filter((rate) => rate.date >= from))
    .reverse()
    .map((rate) => ({ date: rate.date, value: Number(rate.usdToEur) }))
    .filter((point) => Number.isFinite(point.value));
  const first = points[0];
  const end = points[points.length - 1];
  if (first === undefined || end === undefined || points.length < 2) {
    return null;
  }
  const values = points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.2, 0.001);
  return (
    <div className="grid gap-3 border-b border-border">
      <CardHead
        title="Taux USD vers EUR"
        description={`Cours de référence de la BCE, du ${formatRateDate(first.date)} au ${formatRateDate(end.date)}`}
        actions={
          <ChartLegend items={[{ label: '1 $ en euros', color: 'chart-4', shape: 'dot' }]} />
        }
      />
      <div className="px-4 pb-4">
        <TimeChart
          categories={points.map((point) => formatRateDay(point.date))}
          lines={[
            {
              key: 'usd-eur',
              label: '1 $ en euros',
              color: 'chart-4',
              values,
              marker: 'circle',
            },
          ]}
          ariaLabel={`Taux dollar vers euro du ${formatRateDate(first.date)} au ${formatRateDate(end.date)} : entre ${formatRateShort(min)} et ${formatRateShort(max)}, dernier taux ${formatRateShort(end.value)}.`}
          formatValue={formatRateShort}
          ticks={4}
          yMin={min - pad}
          yMax={max + pad}
          width={1100}
          height={230}
          padLeft={60}
        />
      </div>
    </div>
  );
}

export function CatalogPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [tab, setTab] = useState<CatalogTab>('models');
  const [providerFilter, setProviderFilter] = useState<ProviderFilter>('all');
  const [modal, setModal] = useState<'model' | 'rate' | null>(null);
  // Catalog entry whose tariff versions are open, if any.
  const [tariffModel, setTariffModel] = useState<Model | null>(null);
  // Catalog entry whose capabilities are being edited, if any.
  const [editModel, setEditModel] = useState<Model | null>(null);

  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => internalFetch<{ models: Model[] }>('/internal/models'),
  });
  const rates = useQuery({
    queryKey: ['rates'],
    queryFn: () => internalFetch<{ rates: Rate[] }>('/internal/exchange-rates'),
  });

  const refreshRate = useMutation({
    mutationFn: () => internalSend('POST', '/internal/exchange-rates/refresh'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rates'] });
      notify('Taux BCE rafraîchi');
    },
    onError: () => {
      notify('Échec de récupération du taux BCE');
    },
  });

  const modelList = models.data?.models ?? [];
  const visibleModels =
    providerFilter === 'all'
      ? modelList
      : modelList.filter((model) => model.provider === providerFilter);
  const rateList = useMemo(() => sortRatesDesc(rates.data?.rates ?? []), [rates.data]);
  const ratesPage = usePagination(rateList, RATES_PAGE_SIZE);

  // Indicators, computed from the lists already loaded.
  const inactiveCount = modelList.filter((model) => !model.active).length;
  const providers = providerSchema.options.filter((provider) =>
    modelList.some((model) => model.provider === provider),
  );
  const current = latestRate(rateList);
  const before =
    current !== undefined
      ? rateOn(rateList, shiftIsoDate(current.date, -RATE_TREND_DAYS))
      : undefined;
  const change =
    current !== undefined && before !== undefined
      ? formatRateChange(before.usdToEur, current.usdToEur)
      : null;

  const modelsFoot = models.isLoading
    ? 'Chargement…'
    : modelList.length === 0
      ? 'aucune entrée'
      : inactiveCount === 0
        ? 'tous actifs'
        : `${inactiveCount} désactivé${inactiveCount > 1 ? 's' : ''}`;

  return (
    <>
      <PageChrome crumbs={[{ label: 'Catalogue' }]} />
      <PageHead title="Catalogue" subtitle="Modèles d’IA, services SMS, tarifs et taux de change" />

      <Row4 aria-label="Indicateurs du catalogue">
        <StatCard
          label="Modèles et services"
          value={models.isLoading ? '—' : String(modelList.length)}
          foot={modelsFoot}
        />
        <StatCard
          label="Fournisseurs"
          value={models.isLoading ? '—' : String(providers.length)}
          foot={
            models.isLoading ? 'Chargement…' : providers.length > 0 ? providers.join(', ') : 'aucun'
          }
        />
        <StatCard
          label="Taux USD vers EUR"
          value={current !== undefined ? formatRateShort(Number(current.usdToEur)) : '—'}
          foot={
            current !== undefined
              ? `${formatRateDate(current.date)}, source ${rateSourceLabel(current.source)}`
              : rates.isLoading
                ? 'Chargement…'
                : 'aucun taux connu'
          }
        />
        <StatCard
          label={`Écart sur ${RATE_TREND_DAYS} jours`}
          value={change ?? '—'}
          foot={
            current !== undefined && before !== undefined
              ? `de ${formatRateShort(Number(before.usdToEur))} le ${formatRateDay(before.date)} à ${formatRateShort(Number(current.usdToEur))}`
              : rates.isLoading
                ? 'Chargement…'
                : `aucun taux ${RATE_TREND_DAYS} jours plus tôt`
          }
        />
      </Row4>

      <Card>
        <CardBar
          start={
            <TableTabs
              aria-label="Sections du catalogue"
              value={tab}
              onValueChange={setTab}
              items={[
                { value: 'models', label: 'Modèles et services', count: modelList.length },
                { value: 'fx', label: 'Taux de change USD vers EUR', count: rateList.length },
              ]}
            />
          }
          end={
            tab === 'models' ? (
              <>
                <NativeSelect
                  controlSize="sm"
                  wrapperClassName="w-48"
                  aria-label="Filtrer par fournisseur"
                  value={providerFilter}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === 'all') {
                      setProviderFilter('all');
                      return;
                    }
                    const parsed = providerSchema.safeParse(value);
                    if (parsed.success) {
                      setProviderFilter(parsed.data);
                    }
                  }}
                >
                  <option value="all">Tous les fournisseurs</option>
                  {providerSchema.options.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setModal('model');
                  }}
                >
                  <Plus aria-hidden="true" />
                  Ajouter un modèle
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    refreshRate.mutate();
                  }}
                  disabled={refreshRate.isPending}
                >
                  <RefreshCw aria-hidden="true" />
                  Rafraîchir depuis la BCE
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setModal('rate');
                  }}
                >
                  <Plus aria-hidden="true" />
                  Saisir un taux
                </Button>
              </>
            )
          }
        />

        {tab === 'models' ? (
          <>
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Modèle ou service</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>Devise des tarifs</TableHead>
                  <TableHead>Capacités</TableHead>
                  <TableHead kind="act" className="w-[180px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleModels.map((model) => (
                  <TableRow key={model.id}>
                    <TableCell kind="name">{model.displayName}</TableCell>
                    <TableCell>
                      <Tag>{model.provider}</Tag>
                    </TableCell>
                    <TableCell kind="mono">{model.currency}</TableCell>
                    <TableCell>
                      <FeatureTags model={model} />
                    </TableCell>
                    <TableCell kind="act">
                      <div className="inline-flex gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          aria-label={`Capacités de ${model.displayName}`}
                          onClick={() => {
                            setEditModel(model);
                          }}
                        >
                          Capacités
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          aria-label={`Tarifs de ${model.displayName}`}
                          onClick={() => {
                            setTariffModel(model);
                          }}
                        >
                          Tarifs
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {models.isLoading ? <TableEmptyRow colSpan={5}>Chargement…</TableEmptyRow> : null}
                {modelList.length === 0 && !models.isLoading ? (
                  <TableEmptyRow colSpan={5}>Aucun modèle au catalogue.</TableEmptyRow>
                ) : null}
                {modelList.length > 0 && visibleModels.length === 0 ? (
                  <TableEmptyRow colSpan={5}>Aucun modèle de ce fournisseur.</TableEmptyRow>
                ) : null}
              </TableBody>
            </Table>
            <CardFoot
              start={`${visibleModels.length} ${visibleModels.length > 1 ? 'modèles et services' : 'modèle ou service'}`}
              end="Les tarifs en dollars sont convertis en euros au taux du jour"
            />
          </>
        ) : (
          <>
            <RateChart rates={rateList} />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead kind="num">Taux</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ratesPage.pageItems.map((rate) => (
                  <TableRow key={`${rate.date}-${rate.source}`}>
                    <TableCell kind="mono">{formatRateDate(rate.date)}</TableCell>
                    <TableCell kind="num">{formatRate(rate.usdToEur)}</TableCell>
                    <TableCell>
                      <Tag>{rateSourceLabel(rate.source)}</Tag>
                    </TableCell>
                  </TableRow>
                ))}
                {rates.isLoading ? <TableEmptyRow colSpan={3}>Chargement…</TableEmptyRow> : null}
                {rateList.length === 0 && !rates.isLoading ? (
                  <TableEmptyRow colSpan={3}>Aucun taux de change enregistré.</TableEmptyRow>
                ) : null}
              </TableBody>
            </Table>
            <Pager itemLabel="taux" {...ratesPage.pagerProps} />
          </>
        )}
      </Card>

      <AddModelDialog
        open={modal === 'model'}
        onClose={() => {
          setModal(null);
        }}
        onCreated={(created) => {
          setTariffModel(created);
        }}
      />
      <RateDialog
        open={modal === 'rate'}
        onClose={() => {
          setModal(null);
        }}
      />
      {editModel !== null ? (
        <EditFeaturesDialog
          model={editModel}
          onClose={() => {
            setEditModel(null);
          }}
        />
      ) : null}
      {tariffModel !== null ? (
        <TariffsDialog
          model={tariffModel}
          onClose={() => {
            setTariffModel(null);
          }}
        />
      ) : null}
    </>
  );
}
